import { RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

const OLDER_PAGE_PREFETCH_MARGIN = '200px 0px 0px 0px';

export default function useOlderPageSentinel<TSentinel extends HTMLElement>({
  hasOlder,
  hasOlderLoadFailed,
  isLoadingOlder,
  onLoadOlder,
  scrollRef,
}: {
  hasOlder: boolean;
  hasOlderLoadFailed: boolean;
  isLoadingOlder: boolean;
  onLoadOlder: () => void;
  scrollRef: RefObject<HTMLElement | null>;
}) {
  const sentinelRef = useRef<TSentinel>(null);
  const isArmedRef = useRef(true);
  const isSentinelVisibleRef = useRef(false);
  const latestStateRef = useRef({ hasOlderLoadFailed, isLoadingOlder, onLoadOlder });

  useLayoutEffect(() => {
    latestStateRef.current = { hasOlderLoadFailed, isLoadingOlder, onLoadOlder };
  }, [hasOlderLoadFailed, isLoadingOlder, onLoadOlder]);

  // Disarmed after each fetch until the reader scrolls, so a visible sentinel cannot fetch in a loop.
  const loadOlderPageIfArmed = useCallback(() => {
    const latest = latestStateRef.current;
    const canFetch =
      isSentinelVisibleRef.current &&
      isArmedRef.current &&
      !latest.isLoadingOlder &&
      !latest.hasOlderLoadFailed;
    if (!canFetch) return;

    isArmedRef.current = false;
    latest.onLoadOlder();
  }, []);

  const rearm = useCallback(() => {
    isArmedRef.current = true;
  }, []);

  useEffect(() => {
    const scroller = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!hasOlder || !scroller || !sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isSentinelVisibleRef.current = entry.isIntersecting;
        loadOlderPageIfArmed();
      },
      { root: scroller, rootMargin: OLDER_PAGE_PREFETCH_MARGIN },
    );
    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      isSentinelVisibleRef.current = false;
    };
  }, [hasOlder, loadOlderPageIfArmed, scrollRef]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (isLoadingOlder || !scroller) return;

    // A pane that still fits its history has no scrollbar to re-arm the sentinel with.
    const canScroll = scroller.scrollHeight > scroller.clientHeight;
    if (canScroll) return;

    isArmedRef.current = true;
    loadOlderPageIfArmed();
  }, [isLoadingOlder, loadOlderPageIfArmed, scrollRef]);

  return { rearm, sentinelRef };
}
